import React, { useState, useEffect } from 'react';
import { X, Save, MapPin } from 'lucide-react';
import { FaWrench } from "react-icons/fa6";
import { addDoc, collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useNavigate } from 'react-router-dom';

// Modal simple sans shadcn pour éviter les problèmes de z-index
const SimpleModal = ({ isOpen, onClose, children, title }) => {
  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
      style={{ zIndex: 10000 }}
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-xl font-semibold">{title}</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full"
            type="button"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-4">
          {children}
        </div>
      </div>
    </div>
  );
};

const TechnicalTicketModal = ({ 
  isOpen, 
  onClose, 
  zone = null,                    // Zone pré-sélectionnée (depuis la carte)
  gpsLocation = null,             // GPS pré-sélectionné (depuis la carte)
  onSelectLocationFromMap,        // Callback pour changer de zone (sur la carte)
  onSelectGPSLocation,            // Callback pour sélectionner GPS (sur la carte)
  onSuccess,
  fromDashboard = false,          // Nouveau : indique si on vient du tableau de bord
  initialData = null              // Nouveau : données initiales depuis le tableau de bord
}) => {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('moyenne');
  const [subType, setSubType] = useState('infrastructure');
  const [saving, setSaving] = useState(false);
  
  // Pour le mode tableau de bord
  const [zones, setZones] = useState([]);
  const [selectedZoneId, setSelectedZoneId] = useState('');
  const [locationType, setLocationType] = useState('zone'); // 'zone' ou 'gps'

  // Charger les zones si on vient du tableau de bord
  useEffect(() => {
    if (fromDashboard && isOpen) {
      loadZones();
    }
  }, [fromDashboard, isOpen]);

  // Charger les zones depuis Firebase
  const loadZones = async () => {
    try {
      const zonesSnapshot = await getDocs(collection(db, 'lieux'));
      const zonesData = [];
      zonesSnapshot.forEach((doc) => {
        zonesData.push({
          id: doc.id,
          ...doc.data()
        });
      });
      setZones(zonesData);
    } catch (error) {
      console.error('Erreur chargement zones:', error);
    }
  };

  // Initialiser avec les données si fournies
useEffect(() => {
  if (isOpen) {
    if (initialData) {
      // Restaurer les données depuis le tableau de bord
      setTitle(initialData.title || '');
      setDescription(initialData.description || '');
      setPriority(initialData.priority || 'moyenne');
      setSubType(initialData.subType || 'infrastructure');
      
      // Si on a une localisation GPS dans initialData
      if (initialData.gpsLocation) {
        setLocationType('gps');
        // On n'a pas besoin de stocker le GPS, il sera créé directement
      }
    } else {
      // Réinitialiser pour un nouveau ticket
      setTitle('');
      setDescription('');
      setPriority('moyenne');
      setSubType('infrastructure');
      setSelectedZoneId('');
      setLocationType('zone');
    }
  }
}, [isOpen, initialData]);

  const handleSelectGPSFromDashboard = () => {
    // Sauvegarder les données actuelles
    const ticketData = {
      title,
      description,
      priority,
      subType,
      fromDashboard: true
    };
    
    // Naviguer vers la carte en mode sélection GPS
    navigate('/carte', { 
      state: { 
        selectingGPSForTechnical: true,
        technicalTicketData: ticketData
      } 
    });
    
    onClose();
  };

  const handleSubmit = async () => {
    // Validation
    if (!title.trim() || !description.trim()) {
      alert('Veuillez remplir tous les champs obligatoires');
      return;
    }

    // Validation localisation selon le contexte
    if (fromDashboard) {
      if (locationType === 'zone' && !selectedZoneId) {
        alert('Veuillez sélectionner une zone');
        return;
      }
// Si GPS depuis dashboard
if (locationType === 'gps') {
  // Si on a déjà les coordonnées GPS (retour de la carte)
  if (initialData?.gpsLocation) {
    // Continuer avec la création du ticket
    // Le GPS sera ajouté plus bas dans le code
  } else {
    // Sinon, rediriger vers la carte
    handleSelectGPSFromDashboard();
    return;
  }
}
    } else {
      // Depuis la carte, vérifier qu'on a une localisation
      if (!zone && !gpsLocation) {
        alert('Veuillez sélectionner une localisation');
        return;
      }
    }

    setSaving(true);
    try {
      const now = new Date().toISOString();
      
      const newTicket = {
        type: 'technique',
        subType: subType,
        title: title.trim(),
        message: description.trim(),
        priority: priority,
        dateCreation: now,
        dateModification: now,
        acteur: 'Utilisateur',
        statut: 'ouvert',
        historique: [{
          date: now,
          acteur: 'Utilisateur',
          action: 'creation',
          message: `Création du ticket technique - ${getSubTypeLabel(subType)}`
        }]
      };


// Ajouter la localisation
if (fromDashboard && selectedZoneId) {
  // Depuis tableau de bord avec zone sélectionnée
  const selectedZone = zones.find(z => z.id === selectedZoneId);
  if (selectedZone) {
    newTicket.zoneId = selectedZone.id;
    newTicket.zoneName = selectedZone.name;
  }
} else if (fromDashboard && locationType === 'gps' && initialData?.gpsLocation) {
  // GPS depuis tableau de bord (retour de la carte)
  newTicket.gpsLocation = {
    latitude: initialData.gpsLocation.lat,
    longitude: initialData.gpsLocation.lng
  };
} else if (gpsLocation) {
  // GPS depuis la carte directement
  newTicket.gpsLocation = {
    latitude: gpsLocation.lat,
    longitude: gpsLocation.lng
  };
} else if (zone) {
  // Zone depuis la carte
  newTicket.zoneId = zone.id;
  newTicket.zoneName = zone.name;
}
      


      await addDoc(collection(db, 'tickets'), newTicket);
      
      if (onSuccess) {
        onSuccess();
      }

      onClose();
      alert('Ticket technique créé avec succès');
      
    } catch (error) {
      console.error('Erreur lors de la création du ticket technique:', error);
      alert('Erreur lors de la création du ticket. Veuillez réessayer.');
    } finally {
      setSaving(false);
    }
  };

  const getSubTypeLabel = (value) => {
    const labels = {
      'infrastructure': 'Infrastructure',
      'eau_alimentation': 'Eau & Alimentation',
      'equipements': 'Équipements',
      'terrain': 'Terrain',
      'autres': 'Autres'
    };
    return labels[value] || value;
  };

  const getSubTypeDescription = (value) => {
    const descriptions = {
      'infrastructure': 'Clôtures, portails, boxes, abris, toiture...',
      'eau_alimentation': 'Abreuvoirs, irrigation, mangeoires, stockage...',
      'equipements': 'Matériel équestre, outils, véhicules, éclairage...',
      'terrain': 'Drainage, pâtures, chemins, carrière, manège...',
      'autres': 'Autre type d\'intervention technique...'
    };
    return descriptions[value] || '';
  };

  const getPlaceholder = () => {
    const placeholders = {
      'infrastructure': 'Ex: Réparation clôture nord, Fuite toiture box 3...',
      'eau_alimentation': 'Ex: Abreuvoir défectueux, Problème pompe...',
      'equipements': 'Ex: Révision van, Réparation éclairage...',
      'terrain': 'Ex: Drainage prairie, Entretien carrière...',
      'autres': 'Ex: Description de l\'intervention...'
    };
    return placeholders[subType] || 'Ex: Intervention...';
  };

  return (
    <SimpleModal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <div className="flex items-center gap-2">
          <FaWrench className="h-5 w-5 text-purple-600" />
          Nouveau ticket technique
        </div>
      }
    >
      <div className="space-y-6">
        {/* Type d'intervention technique */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Type d'intervention *
          </label>
          <div className="space-y-2">
            {['infrastructure', 'eau_alimentation', 'equipements', 'terrain', 'autres'].map((type) => (
              <label 
                key={type}
                className="flex items-center p-3 border-2 rounded-lg cursor-pointer transition-all hover:bg-gray-50"
              >
                <input
                  type="radio"
                  name="technicalSubType"
                  value={type}
                  checked={subType === type}
                  onChange={(e) => setSubType(e.target.value)}
                  className="mr-3 text-purple-600 focus:ring-purple-500"
                />
                <div className="flex-1">
                  <span className="font-medium">{getSubTypeLabel(type)}</span>
                  <p className="text-xs text-gray-500 mt-1">
                    {getSubTypeDescription(type)}
                  </p>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Localisation - Version adaptative */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Localisation *
          </label>
          
          {fromDashboard ? (
            // Version tableau de bord : choix zone ou GPS
            <div className="space-y-3">
              <div className="flex gap-3">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="locationType"
                    value="zone"
                    checked={locationType === 'zone'}
                    onChange={(e) => setLocationType(e.target.value)}
                    className="mr-2"
                  />
                  <span className="text-sm">Zone</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="locationType"
                    value="gps"
                    checked={locationType === 'gps'}
                    onChange={(e) => setLocationType(e.target.value)}
                    className="mr-2"
                  />
                  <span className="text-sm">Position GPS précise</span>
                </label>
              </div>
              
              {locationType === 'zone' ? (
                <select
                  value={selectedZoneId}
                  onChange={(e) => setSelectedZoneId(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">Sélectionner une zone...</option>
                  {zones.map(zone => (
                    <option key={zone.id} value={zone.id}>
                      {zone.name} {zone.type && `(${zone.type})`}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-center">
                  <MapPin className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                  <p className="text-sm text-blue-800 mb-2">
                    Sélection GPS sur la carte
                  </p>
                  <p className="text-xs text-blue-600">
                    En cliquant sur "Créer", vous serez redirigé vers la carte pour placer le point GPS
                  </p>
                </div>
              )}
            </div>
          ) : (
            // Version carte : affichage de la localisation sélectionnée
            <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
              {zone && !gpsLocation && (
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Zone: {zone.name}</p>
                    <p className="text-sm text-gray-600">Type: {zone.type}</p>
                  </div>
                  <div className="flex gap-2">
                    {onSelectLocationFromMap && (
                      <button
                        type="button"
                        onClick={() => onSelectLocationFromMap({ title, description, priority, subType })}
                        className="px-3 py-1 text-sm bg-purple-100 text-purple-700 rounded hover:bg-purple-200"
                      >
                        Changer zone
                      </button>
                    )}
                    {onSelectGPSLocation && (
                      <button
                        type="button"
                        onClick={() => onSelectGPSLocation({ title, description, priority, subType })}
                        className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                      >
                        GPS précis
                      </button>
                    )}
                  </div>
                </div>
              )}
              
              {gpsLocation && (
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      Position GPS précise
                    </p>
                    <p className="text-xs text-gray-600">
                      Lat: {gpsLocation.lat.toFixed(6)}, Lng: {gpsLocation.lng.toFixed(6)}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    {onSelectLocationFromMap && (
                      <button
                        type="button"
                        onClick={() => onSelectLocationFromMap({ title, description, priority, subType })}
                        className="px-3 py-1 text-sm bg-purple-100 text-purple-700 rounded hover:bg-purple-200"
                      >
                        Choisir zone
                      </button>
                    )}
                    {onSelectGPSLocation && (
                      <button
                        type="button"
                        onClick={() => onSelectGPSLocation({ title, description, priority, subType })}
                        className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                      >
                        Modifier GPS
                      </button>
                    )}
                  </div>
                </div>
              )}
              
              {!zone && !gpsLocation && (
                <div className="text-center py-2">
                  <p className="text-sm text-gray-600 mb-3">Aucune localisation sélectionnée</p>
                  <div className="flex gap-2 justify-center">
                    {onSelectLocationFromMap && (
                      <button
                        type="button"
                        onClick={() => onSelectLocationFromMap({ title, description, priority, subType })}
                        className="px-4 py-2 text-sm bg-purple-600 text-white rounded hover:bg-purple-700"
                      >
                        Sélectionner une zone
                      </button>
                    )}
                    {onSelectGPSLocation && (
                      <button
                        type="button"
                        onClick={() => onSelectGPSLocation({ title, description, priority, subType })}
                        className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                      >
                        Position GPS précise
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          
          <p className="text-xs text-gray-500 mt-1">
            {fromDashboard 
              ? "Choisissez une zone dans la liste ou placez un point GPS sur la carte"
              : "Choisissez une zone ou cliquez sur la carte pour une position GPS précise"
            }
          </p>
        </div>

        {/* Titre */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Titre de l'intervention *
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
            placeholder={getPlaceholder()}
            required
          />
        </div>

        {/* Priorité */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Priorité
          </label>
          <div className="grid grid-cols-3 gap-2">
            <button
              type="button"
              onClick={() => setPriority('basse')}
              className={`p-3 rounded-lg border-2 transition-all ${
                priority === 'basse' 
                  ? 'border-green-500 bg-green-50 text-green-700' 
                  : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
              }`}
            >
              <span className="font-medium">Basse</span>
              <p className="text-xs mt-1">Peut attendre</p>
            </button>
            
            <button
              type="button"
              onClick={() => setPriority('moyenne')}
              className={`p-3 rounded-lg border-2 transition-all ${
                priority === 'moyenne' 
                  ? 'border-orange-500 bg-orange-50 text-orange-700' 
                  : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
              }`}
            >
              <span className="font-medium">Moyenne</span>
              <p className="text-xs mt-1">À planifier</p>
            </button>
            
            <button
              type="button"
              onClick={() => setPriority('haute')}
              className={`p-3 rounded-lg border-2 transition-all ${
                priority === 'haute' 
                  ? 'border-red-500 bg-red-50 text-red-700' 
                  : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
              }`}
            >
              <span className="font-medium">Haute</span>
              <p className="text-xs mt-1">Urgent</p>
            </button>
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Description du problème *
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
            placeholder="Décrivez le problème technique et l'intervention nécessaire..."
            required
          />
        </div>

        {/* Message d'information selon la priorité */}
        <div 
          className={`p-3 rounded-lg border ${
            priority === 'haute' 
              ? 'bg-red-50 border-red-300 text-red-700' 
              : priority === 'moyenne'
              ? 'bg-orange-50 border-orange-300 text-orange-700'
              : 'bg-green-50 border-green-300 text-green-700'
          }`}
        >
          <p className="text-sm font-medium">
            {priority === 'haute' && "🚨 Priorité haute : Intervention urgente requise"}
            {priority === 'moyenne' && "⚠️ Priorité moyenne : À planifier dans les prochains jours"}
            {priority === 'basse' && "✓ Priorité basse : Intervention non urgente"}
          </p>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
            disabled={saving}
          >
            Annuler
          </button>
          <button 
            onClick={handleSubmit}
            disabled={saving || !title.trim() || !description.trim() || 
                     (fromDashboard && locationType === 'zone' && !selectedZoneId)}
            className={`px-4 py-2 rounded-md flex items-center gap-2 ${
              saving || !title.trim() || !description.trim() || 
              (fromDashboard && locationType === 'zone' && !selectedZoneId)
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-purple-600 text-white hover:bg-purple-700'
            }`}
          >
            {saving ? (
              <>
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                Création...
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                {fromDashboard && locationType === 'gps' ? 'Continuer sur la carte' : 'Créer le ticket'}
              </>
            )}
          </button>
        </div>
      </div>
    </SimpleModal>
  );
};

export default TechnicalTicketModal;